import { useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

/**
 * Modal for viewing, searching, and selecting users to assign to a security group.
 * - show {boolean}: Whether the modal is visible.
 * - onHide {() => void}: Callback to close the modal.
 * - groupUsers {Array<Object>}: Users currently assigned to the group.
 * - availableUsers {Array<Object>}: Users available for assignment.
 * - selectedUsers {number[]}: Array of selected user IDs.
 * - onSelectionChange {(ids: number[]) => void}: Setter for selectedUsers.
 * - onSave {(ids: number[]) => Promise<void> | void}: Called when saving the current selection.
 * @returns {JSX.Element} A modal with a selectable users table and actions.
 */
const UserSelectionModal = ({
  show,
  onHide,
  groupUsers,
  availableUsers,
  selectedUsers,
  onSelectionChange,
  onSave
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const allUsers = [
    ...groupUsers.map(user => ({ ...user, isInGroup: true })),
    ...availableUsers.map(user => ({ ...user, isInGroup: false }))
  ];

  const filteredUsers = allUsers.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.FirstName?.toLowerCase().includes(searchLower) ||
      user.LastName?.toLowerCase().includes(searchLower) ||
      user.UserEmail?.toLowerCase().includes(searchLower) ||
      user.Company?.toLowerCase().includes(searchLower) ||
      user.Department?.toLowerCase().includes(searchLower)
    );
  });

  /**
   * Toggles a single user's selection on/off.
   * @param {number} userId - The unique ID of the user.
   * @param {boolean} checked - Whether the user should be selected.
   * @returns {void}
   */
  const handleUserToggle = (userId, checked) => {
    if (checked) {
      onSelectionChange([...selectedUsers, userId]);
    } else {
      onSelectionChange(selectedUsers.filter(id => id !== userId));
    }
  };

  /**
   * Selects or clears all currently filtered users.
   * @param {boolean} checked - If true, selects all filtered users; otherwise clears selection.
   * @returns {void}
   */
  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange(filteredUsers.map(user => user.UserID));
    } else {
      onSelectionChange([]);
    }
  };

  /**
   * Persists the current selection by invoking onSave.
   * Shows a submitting state while the save is in progress.
   * @returns {Promise<void>}
   */
  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      await onSave(selectedUsers);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Renders a checkbox cell for selecting an individual user row.
   * @param {Object} rowData - The row object for the user.
   * @returns {JSX.Element} A checkbox reflecting the row's selection state.
   */
  const checkboxTemplate = (rowData) => {
    const isSelected = selectedUsers.includes(rowData.UserID);
    return (
      <Form.Check
        type="checkbox"
        checked={isSelected}
        onChange={(e) => handleUserToggle(rowData.UserID, e.target.checked)}
      />
    );
  };

  /**
   * Renders a status badge indicating if the user is already in the group.
   * @param {Object} rowData - The row object for the user.
   * @returns {JSX.Element} Badge showing "Current Member" or "Available".
   */
  const statusTemplate = (rowData) => {
    return rowData.isInGroup ? (
      <span className="badge bg-primary">Current Member</span>
    ) : (
      <span className="badge bg-secondary">Available</span>
    );
  };

  const allSelected = filteredUsers.length > 0 && filteredUsers.every(user => selectedUsers.includes(user.UserID));
  const someSelected = filteredUsers.some(user => selectedUsers.includes(user.UserID));

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Manage Group Users</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <Form.Check
            type="checkbox"
            label={`Select All (${filteredUsers.length} users)`}
            checked={allSelected}
            ref={input => {
              if (input) input.indeterminate = someSelected && !allSelected;
            }}
            onChange={(e) => handleSelectAll(e.target.checked)}
          />
        </div>

        <Alert variant="info" className="small">
          <strong>Selected:</strong> {selectedUsers.length} users will be members of this group.
        </Alert>

        <div className="datatable-custom">
          <div className="m-0 justify-content-end d-flex align-items-end p-3">
            <input
              type="text"
              className="m-0 col-2 card d-flex align-items-end p-2"
              placeholder="Type to Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <span className="p-inputgroup-addon">
              <i className="pi pi-search"></i>
            </span>
          </div>
          <div style={{ height: '400px', overflow: 'auto' }}>
            <DataTable
              value={filteredUsers}
              removableSort
              tableClassName="table dataTable no-footer table-striped"
              emptyMessage="No users found"
              scrollable
              scrollHeight="100%"
            >
            <Column
              body={checkboxTemplate}
              header=""
              style={{ width: '50px' }}
            />
            <Column
              field="FirstName"
              header="First Name"
              sortable
            />
            <Column
              field="LastName"
              header="Last Name"
              sortable
            />
            <Column
              field="UserEmail"
              header="Email"
              sortable
            />
            <Column
              field="Company"
              header="Company"
              sortable
            />
            <Column
              field="Department"
              header="Department"
              sortable
            />
            <Column
              body={statusTemplate}
              header="Status"
              style={{ width: '120px' }}
            />
            </DataTable>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          className="btn2"
          onClick={onHide}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          className="btn2"
          onClick={handleSave}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : `Save (${selectedUsers.length} users)`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UserSelectionModal;
